import { useEffect, useState } from "react";
import { Trophy, X, Sparkles, ChevronRight } from "lucide-react";

/**
 * Fullscreen "LEVEL UP!" overlay — appears once when the driver's dashboard
 * detects that they've crossed a new level tier. Uses CSS-only confetti
 * (deterministic random) so no runtime deps.
 *
 * Props:
 *   levelUp: { previous_level, previous_name, new_level, new_name, xp }
 *   onClose: () => void
 */
export default function LevelUpOverlay({ levelUp, onClose }) {
  const [phase, setPhase] = useState("enter"); // enter | show | exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("show"), 40);
    // Auto-dismiss after 8s
    const t2 = setTimeout(() => setPhase("exit"), 8000);
    const t3 = setTimeout(onClose, 8500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setPhase("exit");
    setTimeout(onClose, 400);
  };

  if (!levelUp) return null;

  // Generate deterministic confetti positions
  const confetti = Array.from({ length: 60 }).map((_, i) => {
    const seed = (i * 9301 + 49297) % 233280;
    const rand = (s) => ((s * 1103 + 1234567) % 100) / 100;
    const left = rand(seed);
    const delay = rand(seed + 1);
    const duration = 2 + rand(seed + 2) * 3;
    const rotation = rand(seed + 3) * 360;
    const size = 6 + rand(seed + 4) * 10;
    const colors = ["#f59e0b", "#ef4444", "#a855f7", "#06b6d4", "#22c55e", "#ec4899"];
    const color = colors[i % colors.length];
    return { left, delay, duration, rotation, size, color, i };
  });

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-6 transition-all duration-500 ${
        phase === "exit" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        background: "radial-gradient(ellipse at center, rgba(88,28,135,0.85) 0%, rgba(0,0,0,0.95) 70%)",
        backdropFilter: "blur(20px)",
      }}
      onClick={dismiss}
      data-testid="level-up-overlay"
      role="dialog"
      aria-label="Awans na nowy poziom"
    >
      {/* Confetti layer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map((c) => (
          <div
            key={c.i}
            className="absolute -top-8 rounded-sm"
            style={{
              left: `${c.left * 100}%`,
              width: `${c.size}px`,
              height: `${c.size}px`,
              background: c.color,
              transform: `rotate(${c.rotation}deg)`,
              animation: `lvlUpConfetti ${c.duration}s ${c.delay}s ease-in forwards`,
              opacity: 0.9,
            }}
          />
        ))}
      </div>

      {/* Rays */}
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{
             background: "conic-gradient(from 0deg, transparent 0deg, rgba(251,191,36,0.35) 10deg, transparent 20deg, transparent 40deg, rgba(168,85,247,0.35) 50deg, transparent 60deg, transparent 80deg, rgba(6,182,212,0.35) 90deg, transparent 100deg)",
             animation: "lvlUpSpin 12s linear infinite",
           }} />

      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative max-w-lg w-full transition-all duration-500 ${
          phase === "enter" ? "scale-50 opacity-0" :
          phase === "exit"  ? "scale-95 opacity-0" :
          "scale-100 opacity-100"
        }`}
      >
        <button
          onClick={dismiss}
          data-testid="btn-close-level-up"
          className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/30 text-white hover:bg-white/25 flex items-center justify-center z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-yellow-400 via-orange-500 via-red-600 to-fuchsia-700 p-1 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.9)]">
          <div className="rounded-[1.75rem] bg-gradient-to-br from-zinc-950 via-purple-950 to-black p-8 text-center">
            {/* Sparkles above */}
            <div className="flex justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" />
              <Sparkles className="w-8 h-8 text-amber-400 animate-pulse" style={{ animationDelay: "150ms" }} />
              <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" style={{ animationDelay: "300ms" }} />
            </div>

            <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-white/50 mt-2">
              // Poziom {levelUp.previous_level} → {levelUp.new_level}
            </div>

            {/* MAIN TITLE */}
            <h2
              className="font-heading font-black text-5xl md:text-6xl uppercase tracking-tight mt-3 leading-none"
              style={{
                background: "linear-gradient(90deg, #fde047, #f59e0b, #ef4444, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 8px 20px rgba(251,191,36,0.5))",
              }}
            >
              LEVEL UP!
            </h2>

            {/* LVL trophy */}
            <div className="my-6 relative flex justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 blur-2xl opacity-70 animate-pulse" />
              </div>
              <div className="relative w-28 h-28 rounded-3xl bg-gradient-to-br from-yellow-300 via-amber-500 to-orange-700 flex items-center justify-center ring-4 ring-yellow-200/50 shadow-2xl">
                <Trophy className="w-14 h-14 text-white drop-shadow-lg" strokeWidth={2} />
                <div className="absolute -bottom-3 -right-3 w-14 h-14 rounded-full bg-gradient-to-br from-yellow-200 to-orange-500 ring-4 ring-zinc-950 flex flex-col items-center justify-center">
                  <div className="text-[7px] font-mono uppercase tracking-widest text-orange-900/80 leading-none">LVL</div>
                  <div className="text-xl font-heading font-black text-orange-950 leading-none mt-0.5">
                    {levelUp.new_level}
                  </div>
                </div>
              </div>
            </div>

            {/* Transition */}
            <div className="flex items-center justify-center gap-3 mt-4 text-white/70">
              <span className="text-sm font-mono">{levelUp.previous_name || "?"}</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-lg font-heading font-black text-yellow-300 uppercase tracking-tight">
                {levelUp.new_name}
              </span>
            </div>

            <div className="text-[11px] font-mono text-white/60 mt-4">
              {Number(levelUp.xp || 0).toLocaleString("pl-PL")} XP
            </div>

            <button
              onClick={dismiss}
              data-testid="btn-confirm-level-up"
              className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-2xl bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 hover:from-yellow-300 hover:via-orange-400 hover:to-red-500 text-white font-bold uppercase tracking-widest text-sm shadow-lg transition-all hover:scale-105"
            >
              Ekstra! Jedziemy dalej
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes lvlUpConfetti {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0.9; }
          100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
        }
        @keyframes lvlUpSpin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
